import React, { useEffect, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import { getAccountUser } from "../../api/account/getAccountUser";
import { User } from '../../types/interfaces/user';
import { getAvatar } from '../../api/user/getAvatar';

interface UserAvatarProps {
  accountId: number;
  userId: number;
  alternateText?: string;
  size?: number;
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  accountId,
  userId,
  alternateText = '??',
  size = 40
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl: string | null = null;

    const fetchUserAndAvatar = async () => {
      try {
        const userData = await getAccountUser(accountId, userId);
        if (!isMounted) return;
        setUser(userData);

        const avatarBlob = await getAvatar(accountId, userId);
        if (!isMounted) return;

        if (avatarBlob) {
          objectUrl = URL.createObjectURL(avatarBlob);
          setAvatarUrl(objectUrl);
        } else {
          setAvatarUrl(null);
        }
      } catch (error) {
        setHasError(true);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchUserAndAvatar();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [accountId, userId]);

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  if (isLoading) {
    return <Skeleton variant="circular" width={size} height={size} />;
  }

  if (hasError || !user || !avatarUrl) {
    return (
      <Avatar sx={{ width: size, height: size }}>
        {user ? getInitials(user.name) : alternateText}
      </Avatar>
    );
  }

  return (
    <Avatar
      src={avatarUrl || undefined}
      alt={user?.name}
      sx={{ width: size, height: size }}
    />
  );
};

export default UserAvatar;
